import type { ReactNode } from 'react';
import { useSite } from '@/app/site-context';
import { useDocumentHead } from '@/lib/seo/useDocumentHead';
import { ContactBlock } from '@/components/marketing/ContactBlock';
import { PageHero } from '@/components/marketing/PageHero';
import { Container, Section } from '@/components/ui';

/**
 * The contact page — the design's "Let's talk scale." section as a full page.
 *
 * Everything below the hero is `ContactBlock`, shared with the homepage's closing block,
 * so the two are the same design by construction rather than by anyone remembering to
 * change both. The form inside it is `InquiryForm`, so the payload, the honeypot and the
 * server-error mapping are likewise defined exactly once.
 */
export function ContactPage(): ReactNode {
  const { settings } = useSite();

  useDocumentHead({
    title: `Contact | ${settings.brand.name}`,
    description: 'Tell us about the role you need to fill.',
    canonicalPath: '/contact',
  });

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let's talk about the role"
        highlight="about the role"
        description={
          settings.contact.responsePromise ||
          'Send us the brief and we will tell you honestly whether we are the right people for it.'
        }
        note="One form, one reply, from the person who would run the search."
      />

      <Section tone="surface" size="compact" className="pt-10 md:pt-14">
        <Container>
          {/* Not lazy: the hero above is short, so the artwork is in the viewport almost
              immediately and a late swap-in beside the form reads as a jump. */}
          <ContactBlock sourcePath="/contact" idPrefix="contact" lazyImage={false} />
        </Container>
      </Section>
    </>
  );
}
